import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

/**
 * The card grid every edit section lays its cards in: 2-col auto-fit,
 * single column below ~580px. Exported so a host that renders ONE section
 * without the collapsible row (the Studio's Edit bar) keeps the same flow.
 *
 * `alignItems: start` — cards size to their own content instead of
 * stretching to the tallest sibling. Without this the music-volume card grew
 * to match the track picker beside it and rendered a large void between its
 * slider and its button.
 */
export const EDIT_CARD_GRID: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  alignItems: "start",
  gap: 14,
};

/**
 * Tiny cost chip rendered next to each edit-card's label. Mono digits,
 * dimmed colour — the operator reads it for "should I wait for this or
 * grab a coffee" without it shouting over the actual title.
 */
export function CostBadge({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <span
      style={{
        fontSize: 10,
        fontFamily: "var(--ql-font-mono)",
        color: "var(--ql-fg-subtle)",
        letterSpacing: ".02em",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/**
 * Collapsible section wrapper for the Edit-clip panel. Groups cards by
 * cost-class with a faint background wash (green = fast, yellow =
 * medium, orange = full pipeline) and a header that toggles the row
 * open/closed. The wash is intentionally near-imperceptible — its job
 * is to give the eye a stable anchor when scanning, not to colour-code
 * actions in a way that feels alarmist.
 *
 * Sections accept any children and lay them out in a 2-col auto-fit
 * grid; if only one card is visible, it occupies the left slot and the
 * right is empty. That's preferable to reshuffling the row whenever
 * `hasTranscript` flips — the spatial position of each control stays
 * consistent.
 */
export function EditSectionRow({
  label,
  costHint,
  tint,
  defaultOpen = true,
  children,
}: {
  label: string;
  costHint: string;
  /** rgba background wash for the section. Keep alpha ≤0.04. */
  tint: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      style={{
        background: tint,
        borderRadius: 12,
        padding: "10px 12px 12px",
        marginBottom: 12,
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          padding: "4px 2px 10px",
          background: "transparent",
          border: "none",
          color: "inherit",
          fontFamily: "inherit",
          cursor: "pointer",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: ".10em",
            textTransform: "uppercase",
            color: "var(--ql-fg)",
          }}
        >
          {open ? (
            <ChevronDown size={13} strokeWidth={2.25} color="var(--ql-fg-muted)" />
          ) : (
            <ChevronRight size={13} strokeWidth={2.25} color="var(--ql-fg-muted)" />
          )}
          {label}
        </span>
        <CostBadge>{costHint}</CostBadge>
      </button>
      {open && <div style={EDIT_CARD_GRID}>{children}</div>}
    </div>
  );
}
